import React, { useState, useEffect } from 'react';
import UploadVoiceStep from './AutopilotSteps/UploadVoiceStep';
import ChooseTemplateStep from './AutopilotSteps/ChooseTemplateStep';
import UpdateMetadataStep from './AutopilotSteps/UpdateMetadataStep';
import GoLiveStep from './AutopilotSteps/GoLiveStep';
import Swal from 'sweetalert2';

// --- ICONS (Keep these here)
const CheckCircle = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
);
const XCircle = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
);
const Loader = ({ className }) => (
  <div className={`w-4 h-4 sm:w-5 sm:h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin ${className}`}></div>
);
const SpinnerIcon = ({ className }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const roboman_api = process.env.REACT_APP_ROBOMAN_API;
const Metadata = () => {
  const [uploadState, setUploadState] = useState('idle');
  const [templateState, setTemplateState] = useState('idle');
  const [metadataState, setMetadataState] = useState('idle');
  const [finalizeState, setFinalizeState] = useState('idle');

  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [clientData, setClientData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // ✅ MODIFIED: This useEffect now also checks user_data for initial state
  useEffect(() => {

    setIsLoading(true);
    setFinalizeState('idle');
    setErrorMessage('');

    // --- Get all possible data sources from localStorage ---
    const savedVoiceDataString = localStorage.getItem('voiceUploadResponse');
    const savedTemplateId = localStorage.getItem('selectedTemplateId');
    const userDataString = localStorage.getItem('user_data');
    let userData = null;
    if (userDataString) {
      try {
        userData = JSON.parse(userDataString);
      } catch (error) {
        console.error("Failed to parse user_data on initial load.", error);
      }

    }

    // --- Initialize Voice State (Step 1) ---
    if (savedVoiceDataString) {
      // Priority 1: Use session-specific voice upload
      try {
        const parsedData = JSON.parse(savedVoiceDataString);
        setUploadedFileId(parsedData.voice_id);
        setUploadState('success');
      } catch {
        localStorage.removeItem('voiceUploadResponse');
        setUploadedFileId(null);
        setUploadState('idle');
      }
    } else if (userData?.user_info?.voice_note_id) {
      // Priority 2: Use profile's default voice note
      setUploadedFileId(userData.user_info.voice_note_id);
      setUploadState('success');
    } else {
      // No voice found
      setUploadedFileId(null);
      setUploadState('idle');
    }

    // --- Initialize Template State (Step 2) ---
    if (savedTemplateId) {
      // Priority 1: Use session-specific template selection
      setSelectedTemplate(savedTemplateId);
      setTemplateState('loaded');
    } else if (userData?.user_info?.proposal_template) {
      // Priority 2: Use profile's default template
      setSelectedTemplate(userData.user_info.proposal_template);
      setTemplateState('loaded');
    } else {
      // No template found
      setSelectedTemplate('');
      setTemplateState('idle');
    }

    // Reset subsequent steps
    setClientData(null);
    setMetadataState('idle');


    setIsLoading(false);
  }, []);


  const handleFinalize = async () => {
    setErrorMessage('');
    setFinalizeState('finalizing');

    const campaignId = localStorage.getItem("campaign_id");
    if (!campaignId) {
      Swal.fire({ icon: 'error', title: 'Submission Failed', text: 'Campaign ID not found.' });
      setFinalizeState('error');
      return;
    }

    const finalPayload = {
      ...clientData,
      campaign_id: campaignId,
      voice_note_id: uploadedFileId,
      template_name: selectedTemplate,
    };

    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${roboman_api}/campaign/update_metadata/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(finalPayload),
      });
      if (!res.ok) throw new Error(`API Error: ${res.status}`);
      const responseData = await res.json();
      if (responseData.status !== 'success') throw new Error("API returned an unsuccessful status.");
      setFinalizeState('success');
      Swal.fire({ icon: 'success', title: 'Autopilot Started!', text: 'Your campaign is now being processed.' });
      localStorage.removeItem('voiceUploadResponse');
      localStorage.removeItem('selectedTemplateId');
    } catch (error) {
      setErrorMessage(error.message);
      setFinalizeState('error');
      Swal.fire({ icon: 'error', title: 'Submission Failed', text: error.message });
    }
  };

  const Step = ({ number, title, description, children, state }) => {
    const isCompleted = state === 'success' || state === 'loaded';
    const statusIcons = { fetching: <Loader />, finalizing: <Loader />, error: <XCircle className="w-6 h-6 text-red-500" /> };
    return (
      <div className="relative pl-8 sm:pl-10 md:pl-12">
        <div className="absolute top-0 left-0 flex flex-col items-center">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${isCompleted ? 'bg-violet-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
            {isCompleted ? <CheckCircle className="w-4 h-4" /> : number}
          </div>
          {number < 4 && <div className="mt-1 sm:mt-2 w-0.5 h-full bg-slate-200"></div>}
        </div>
        <div className={`transition-opacity ${state === 'disabled' ? 'opacity-50' : ''}`}>
          <div className="flex flex-col gap-2 sm:gap-0 sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4">
            <div className='flex-1'>
              <h4 className="font-bold text-base sm:text-lg md:text-xl text-slate-800 mb-1">{title}</h4>
              {description && <p className=" text-xs sm:text-sm text-slate-500 leading-relaxed">{description}</p>}
            </div>
            <div className="flex items-center gap-2 min-h-[20px] sm:min-h-[24px] sm:ml-4">{statusIcons[state]}</div>
          </div>
          <div className={` ${state === 'disabled' ? 'pointer-events-none' : ''}`}>{children}</div>
        </div>
      </div>
    );
  };
  // if (isLoading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-sky-100">
  //       <SpinnerIcon className="h-8 w-8 text-slate-500" />
  //       <span className="ml-3 text-slate-500">Loading profile...</span>
  //     </div>
  //   );
  // }

  
  const shimmer ="relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent";

  const AutopilotSkeleton = () => {
    return (
      <div className="flex min-h-screen bg-gray-50">

      

        {/* Main Content */}
        <main className="flex-1 flex justify-center px-6 py-10">
          <div className="w-full max-w-xl space-y-6">

            {/* Header */}
            <div className="space-y-2">
              <div className={`h-6 w-48 bg-gray-300 rounded ${shimmer}`} />
              <div className={`h-4 w-64 bg-gray-200 rounded ${shimmer}`} />
            </div>

            {/* Step 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
              <div className={`h-4 w-40 bg-gray-300 rounded ${shimmer}`} />
              <div className="flex gap-3">
                <div className={`h-10 w-24 bg-indigo-200 rounded-lg ${shimmer}`} />
                <div className={`h-10 w-24 bg-green-200 rounded-lg ${shimmer}`} />
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border space-y-3">
              <div className={`h-4 w-44 bg-gray-300 rounded ${shimmer}`} />
              <div className={`h-10 w-full bg-gray-200 rounded-lg ${shimmer}`} />
            </div>

            {/* Step 3 – Form */}
            <div className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
              <div className={`h-4 w-36 bg-gray-300 rounded ${shimmer}`} />

              <div className="grid grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`h-10 bg-gray-200 rounded-lg ${shimmer}`}
                  />
                ))}
              </div>

              <div className={`h-10 w-full bg-indigo-300 rounded-lg ${shimmer}`} />
            </div>

            {/* CTA */}
            <div className={`h-12 w-full bg-purple-400 rounded-xl ${shimmer}`} />
          </div>
        </main>

        {/* shimmer keyframes */}
        <style>
          {`
          @keyframes shimmer {
            100% {
              transform: translateX(100%);
            }
          }
        `}
        </style>
      </div>
    );
  }
  
  



  return (
    <>
      {isLoading ?
        < AutopilotSkeleton /> :
        (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold font-Inter opacity-1 bg-gradient-to-r from-gray-700 via-blue-700 to-fuchsia-700  bg-clip-text text-transparent mb-2">Autopilot Workflow</h1>
            <p className="text-sm sm:text-base text-slate-500 mb-6 sm:mb-8">Follow the steps below to configure and launch your campaign.</p>

            <div className="space-y-8 sm:space-y-10 md:space-y-12 pb-6 overflow-visible min-h-[44px]">
              <Step number={1} title="Upload Your Voice Note" state={uploadState}>
                {/* <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full"> */}
                <UploadVoiceStep onUploadSuccess={(fileId) => { setUploadedFileId(fileId); setUploadState('success'); }} onUploadError={(err) => { setErrorMessage(err); setUploadState('error'); }} />
                {/* </div> */}
              </Step>

              <Step number={2} title="Choose a PDF Template" state={uploadState === 'success' ? templateState : 'disabled'}>
                <ChooseTemplateStep selectedTemplate={selectedTemplate} onTemplateSelect={setSelectedTemplate} onTemplatesLoaded={() => setTemplateState('loaded')} onLoadingError={(err) => { setErrorMessage(err); setTemplateState('error'); }} disabled={uploadState !== 'success'} />
              </Step>

              <Step number={3} title="Add Your Details" state={templateState === 'loaded' ? metadataState : 'disabled'}>
                <UpdateMetadataStep disabled={templateState !== 'loaded'} onMetadataSubmit={(formData) => { setClientData(formData); setMetadataState('success'); }}
                  // Add these two props:
                  selectedTemplate={selectedTemplate}
                  uploadedFileId={uploadedFileId}
                />
              </Step>

              {/* <Step number={4} title="Go to Autopilot" state={metadataState === 'success' ? finalizeState : 'disabled'}>
          <GoLiveStep disabled={metadataState !== 'success'} isFinalizing={finalizeState === 'finalizing'} onFinalize={handleFinalize} />
        </Step> */}

              <Step number={4} title="Go to Autopilot">
                {/* <div className="w-full flex flex-col sm:flex-row sm:justify-end gap-3"> */}
                <GoLiveStep />
                {/* </div> */}
              </Step>
            </div>

            {finalizeState === 'success' && (
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-6 sm:mt-8 px-4 py-3 bg-green-100 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5  flex-shrink-0 text-green-600 " />
                <p className="font-semibold text-sm text-green-800">Autopilot Started!</p>
              </div>
            )}

            {errorMessage && (
              <div className="flex items-center justify-center mt-6 p-2 bg-red-100 border border-red-200 rounded-lg">
                <XCircle className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5" />
                <p className="font-semibold text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        )
      }
    </>
  );
};

export default Metadata;